import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Loader2, Lock } from 'lucide-react';
import { getApiBase, getBackendBaseUrl } from '../utils/apiBase';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { formatPersonDateRange } from '../utils/dateDisplay';
import { PublicLayout } from '../components/layout/PublicLayout';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

export default function PublishedMemoryView() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/public/memory/${slug}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setError('This memory view is not available or is private.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(42_28%_98%)]">
        <Loader2 className="h-8 w-8 animate-spin text-[#8b7355]" aria-label="Loading memory view" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[hsl(42_28%_98%)] px-6 text-center">
        <Lock className="h-10 w-10 text-muted-foreground" aria-hidden />
        <p className="font-serif text-xl text-[hsl(24_14%_18%)]">{error || 'Not found'}</p>
        <Link to="/" className="text-sm text-[#8b7355] underline-offset-4 hover:underline">Return home</Link>
      </div>
    );
  }

  const tree = data.tree || {};
  const coverUrl = tree.cover_image_url ? resolveAssetUrl(BACKEND, tree.cover_image_url) : null;

  return (
    <PublicLayout className="min-h-screen bg-gradient-to-b from-[hsl(42_30%_99%)] to-[hsl(38_18%_94%)]" footerVariant="compact">
      <header className="border-b border-[hsl(38_16%_88%)]/80 bg-white/70 backdrop-blur-sm">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <BrandWordmark className="h-7" />
          <span className="rounded-full border border-[hsl(38_16%_86%)] px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            Memory view · read only
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        {coverUrl ? (
          <div className="mb-8 overflow-hidden rounded-2xl border border-[hsl(38_16%_86%)] shadow-sm">
            <img src={coverUrl} alt="" className="max-h-64 w-full object-cover" />
          </div>
        ) : null}

        <div className="text-center">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[hsl(32_10%_42%)]">Published memory</p>
          <h1 className="mt-2 font-serif text-3xl font-normal text-[hsl(24_14%_14%)] sm:text-4xl">{tree.name}</h1>
          {tree.description ? (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[hsl(32_10%_38%)]">{tree.description}</p>
          ) : null}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {(data.people || []).map((person) => (
            <article
              key={person.id}
              className="rounded-2xl border border-[hsl(38_16%_86%)] bg-white/70 p-5 shadow-sm"
            >
              <h2 className="font-serif text-xl text-[hsl(24_14%_16%)]">
                {[person.given_names, person.surname].filter(Boolean).join(' ')}
              </h2>
              {(person.birth_date || person.death_date) ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {[person.birth_date, person.death_date].filter(Boolean).length > 0
                    ? formatPersonDateRange(person)
                    : null}
                </p>
              ) : null}
              {person.featured_memory_excerpt ? (
                <p className="mt-3 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{person.featured_memory_excerpt}</p>
              ) : null}
            </article>
          ))}
        </div>

        {(data.memories || []).length > 0 ? (
          <section className="mt-12">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[hsl(24_14%_16%)]">
              <BookOpen className="h-5 w-5 text-[#8b7355]" aria-hidden />
              Memories
            </h2>
            <ul className="space-y-4" role="list">
              {data.memories.map((m) => (
                <li key={m.id} className="rounded-2xl border border-[hsl(38_16%_86%)] bg-white/80 p-5">
                  <h3 className="font-serif text-lg text-[hsl(24_14%_16%)]">{m.title}</h3>
                  {m.source_date ? <p className="text-xs text-muted-foreground">{m.source_date}</p> : null}
                  {m.excerpt ? (
                    <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_38%)] whitespace-pre-wrap">{m.excerpt}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <p className="mt-12 text-center text-xs text-muted-foreground">
          This is a read-only memory view. Private vault documents and editing tools are never shown here.
        </p>
      </main>
    </PublicLayout>
  );
}
